import {t} from 'i18next';
import React, {PropsWithChildren, ReactElement, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import NobulaLogoIcon from '../../../../../../assets/icons/NobulaLogo.svg';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import Button from '../../../../../incase-domain/components/button/Button';
import {IElementPhotoAnswerQuestion} from '../../../../../incase-domain/elements/question/photoAnswerQuestion';
import {QuestionElementInteractions, IRunFile} from '../../../../../incase-domain/proptypes/runfile';
import {gameConfigSelector, loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {pointsSelector} from '../../../../../store/selectors/runfile.selectors';
import {getBorderStyle} from '../../../../../util/style.utils';
import QuestionLayout from '../common/QuestionLayout';
import {useQuestionRemainingToast} from '../common/useQuestionRemainingToast';
import useQuestionTry from '../useQuestionTry';
import {handleRetryAndResultOnClick} from '../utils/handleClick.question.utils';
import {handleSelectAnswer} from '../utils/handleTryButton.question.utils';
import PhotoQuestionAnswer from './PhotoQuestionAnswer';
import {getAnswerType} from '../common/utils';
import {useOpenComment} from '../common/useOpenComment';
import Modal from '../../../../../incase-domain/components/modals/directory/Modal';

interface PhotoAnswerQuestionElementProps {
    element: IElementPhotoAnswerQuestion;
    savePresentationRunFile: (elementRunFile: QuestionElementInteractions, newPoints?: number) => void;
    device?: DeviceType;
    elementInteractions: QuestionElementInteractions;
}

function PhotoAnswerQuestionElement(props: PropsWithChildren<PhotoAnswerQuestionElementProps>): ReactElement {
    const {element, elementInteractions, savePresentationRunFile, device = DeviceType.DESKTOP} = props;
    const {answers, answersInARow, borderRadius} = element;
    const dispatch = useDispatch();

    const {emitRemainingTriesToast} = useQuestionRemainingToast();
    const presentation = useSelector(loadedPresentationSelector);
    const points = useSelector(pointsSelector);
    const gameConfig = useSelector(gameConfigSelector);

    const questionTry = useQuestionTry<IElementPhotoAnswerQuestion>();

    const [modalState, setModalState] = useState<{isOpen: boolean; index: number} | null>(null);
    const {getCommentProps, toggleComment} = useOpenComment();

    const {wrongAnswer} = elementInteractions;

    const clickedAnswers = elementInteractions.clickedAnswers ? elementInteractions.clickedAnswers : [];
    const markedAnswers = elementInteractions.markedAnswers ? elementInteractions.markedAnswers : [];
    const answered = element.haveTryButton ? elementInteractions.answered : element.singleCorrect && clickedAnswers.length > 0;

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout>;
        if (wrongAnswer !== undefined) {
            timeout = setTimeout(() => {
                savePresentationRunFile({...elementInteractions, wrongAnswer: undefined});
            }, 1000);
        }
        return () => clearTimeout(timeout);
    }, [wrongAnswer, elementInteractions, savePresentationRunFile]);

    function handleAnswerClick(index: number): void {
        if (element.haveTryButton) {
            handleSelectAnswer(elementInteractions, element, index, markedAnswers, savePresentationRunFile, presentation);
        } else {
            handleRetryAndResultOnClick(
                element,
                clickedAnswers,
                index,
                elementInteractions,
                {points} as IRunFile,
                gameConfig,
                savePresentationRunFile,
                emitRemainingTriesToast,
                dispatch,
                presentation
            );
        }
    }

    const isAnswerSelected = (index: number): boolean =>
        !!elementInteractions.markedAnswers?.includes(index) || !!elementInteractions.clickedAnswers?.includes(index);

    const canShowAnswerComment = (index: number, isAnswered: boolean, isSelected: boolean): boolean =>
        !!answers[index]?.comment && isAnswered && (isSelected || element.showAllComments);

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton={element.haveTryButton || device === DeviceType.MOBILE}
            onConfirm={() => questionTry(element, elementInteractions)}
            confirmDisabled={markedAnswers.length === 0}
        >
            <div className={`c-photoAnswerQuestion c-photoAnswerQuestion--col-${device === DeviceType.MOBILE ? '2' : answersInARow}`}>
                {answers.map((answer, index) => {
                    return (
                        <PhotoQuestionAnswer
                            key={`photo-question-answer-${element.id}-${index}`}
                            answer={answer.text}
                            answerType={getAnswerType(answer, answered)}
                            imgSrc={answer.imgSrc || ''}
                            imageFit={element.imageFit}
                            comment={answer.comment}
                            debatableColor={element.debatableColor}
                            correctColor={element.correctColor}
                            incorrectColor={element.incorrectColor}
                            onClick={() => {
                                if (device === DeviceType.MOBILE) {
                                    setModalState({isOpen: true, index});
                                } else {
                                    const isSelected = isAnswerSelected(index);
                                    const willAnswerAfterClick = answered || (!element.haveTryButton && element.singleCorrect);
                                    const willBeSelectedAfterClick =
                                        isSelected || (!answered && !element.haveTryButton && element.singleCorrect);

                                    handleAnswerClick(index);

                                    toggleComment(index, canShowAnswerComment(index, willAnswerAfterClick, willBeSelectedAfterClick));
                                }
                            }}
                            clicked={isAnswerSelected(index)}
                            answered={answered}
                            setImgModalState={() => setModalState({isOpen: true, index})}
                            showAllComments={element.showAllComments}
                            device={device}
                            {...getCommentProps(index, canShowAnswerComment(index, answered, isAnswerSelected(index)))}
                        />
                    );
                })}

                <Modal isOpen={!!modalState?.isOpen} onCancel={() => setModalState(null)} noPadding>
                    <div className="c-photoAnswerQuestion__answerModal">
                        <img
                            src={answers[modalState?.index as number]?.imgSrc || NobulaLogoIcon}
                            alt="answer"
                            style={{
                                borderRadius: getBorderStyle(borderRadius),
                                objectFit: element.imageFit,
                                maxHeight: '65vh',
                            }}
                        />

                        <div className="c-photoAnswerQuestion__answerModal_actions">
                            <Button size="lg" fullWidth variant="soft" onClick={() => setModalState(null)}>
                                {t('common.dismiss')}
                            </Button>
                            <Button
                                variant="solid"
                                color="info"
                                fullWidth
                                size="lg"
                                disabled={answered}
                                onClick={() => {
                                    handleAnswerClick(modalState?.index as number);
                                    setModalState(null);
                                }}
                            >
                                {markedAnswers.includes(modalState?.index as number) ? t('common.unselect') : t('common.select')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            </div>
        </QuestionLayout>
    );
}

export default PhotoAnswerQuestionElement;
